"use client";

import React, { useEffect, useState, useMemo } from "react";
import {
    CategoryFilterBar,
    AllProducts,
} from "../components";
import type { FilterState } from "../components/SidebarFilter";
import { useTenant } from "@/src/contexts/TenantContext";
import type { Category, OutletDetails, Product, Tenant } from "@/src/types/tenant";
import { matchesCurator, matchesPriceRange } from "@/src/utils/filter/productFilter";
import { useServingOutlet } from "@/src/hooks/catalog/useServingOutlet";
import { dedupeToUnique, resolveCuratedToVisible } from "@/src/utils/products/groupCatalog";
import {
    getProductPage,
    getProductFacets,
    type ProductFacets,
    type ProductPagination,
    type ProductPageQuery,
} from "@/src/services/product/productPage.service";

const PAGE_SIZE = 24;

/** Stable empty fallback — see the `outlets ?? NO_OUTLETS` call below. */
const NO_OUTLETS: OutletDetails[] = [];

interface AllProductsClientProps {
    prefix: string;
    tenant: Tenant;
    outlet: OutletDetails | null;
    /** Every landing outlet for the store; length > 1 turns on per-card branch labels. */
    outlets?: OutletDetails[];
    categories: Category[];
    products: Product[];
    /**
     * TRUE when `products` arrived already collapsed to one card per real
     * product. False (the default) means it is the raw cross-branch union and
     * this component must collapse it — see TenantLandingResponse.grouped.
     */
    grouped?: boolean;
    initialCategory?: string;
    initialSortBy?: string;
    /** When set, restrict to these product _ids (a curated "Shop the edit" collection). */
    collectionIds?: string[];
    collectionTitle?: string;
    /** Tenant's configured category order; see StorefrontConfig.productPriority. */
    productPriority?: string[];
}

/** Returns the effective selling price for filter comparison */
function effectivePrice(p: Product): number {
    return p.pricing?.offerPrice ?? p.pricing?.sellingPrice ?? 0;
}

export default function AllProductsClient({
    prefix,
    tenant,
    outlet: outletProp,
    outlets,
    categories,
    products,
    grouped = false,
    initialCategory = "all",
    initialSortBy = "newest",
    collectionIds,
    collectionTitle,
    productPriority,
}: AllProductsClientProps) {
    const { setTenant } = useTenant();

    /* Every product the store stocks, across branches, each appearing once.
     *
     * Collapsed HERE rather than at the rendered list because CategoryFilterBar
     * counts its pills off `baseProducts` — collapsing later would leave a pill
     * reading "Snacks 4" above a two-card grid.
     *
     * Skipped entirely when portal-api already collapsed the payload, which is
     * the normal path. The local rule then runs only on the degraded one, so
     * there is exactly one rule in play at a time. */
    const groupedProducts = useMemo(
        () => (grouped ? products : dedupeToUnique(products)),
        [grouped, products]
    );

    /* Curated collection mode: the seller's picks, in order. Resolved through
     * the grouped catalogue rather than looked up by raw `_id`, or a pick made
     * against the branch that lost its group would silently drop out and the
     * tile would open short. */
    const isCollection = !!collectionIds?.length;
    const baseProducts = useMemo(
        () =>
            isCollection
                ? resolveCuratedToVisible(collectionIds, products, groupedProducts)
                : groupedProducts,
        [isCollection, collectionIds, products, groupedProducts]
    );

    // ── Filter state (lifted from SidebarFilter) ──────────────────────────────
    const [filters, setFilters] = useState<FilterState>({
        category: initialCategory,
        subcategory: "all",
        curators: [],
        priceRanges: [],
    });

    const [activeSortBy, setActiveSortBy] = useState(initialSortBy);

    // Prefer the dedicated outlet object from API; fall back to product-embedded outlet
    const outlet = outletProp ?? products[0]?.outlet ?? null;
    const outletId = outlet?.outletId || "";

    /* The branch this buyer shops from. Every server query below is scoped to it
     * rather than filtered afterwards: this grid paginates, and filtering a page
     * of 24 down to the serving branch would produce short pages and totals that
     * count products the buyer can never reach. */
    /* `products` is the landing union across every outlet, so it is the stock
     * signal. Critical here: this component SCOPES ITS SERVER QUERY to the
     * result, and an outlet with no catalogue would render "Showing 0 of 0"
     * with no client-side valve to save it. */
    /* NO_OUTLETS is a module constant, not an inline `?? []`: a fresh array each
     * render is a new reference and would invalidate useServingOutlet's memo. */
    const servingOutletId = useServingOutlet(outlets ?? NO_OUTLETS, outletId);

    useEffect(() => {
        setTenant({
            storeName: outlet?.outletName || tenant.tenantName,
            logoUrl: tenant.additionalInfo?.logoUrl ?? null,
            outletId,
            prefix,
            minCartValue: outlet?.minCartValue ?? null,
            city: outlet?.city ?? null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Derived filtered product list ─────────────────────────────────────────
    const filteredProducts = useMemo(() => {
        return baseProducts.filter((p) => {
            // Category. `filters.category` is a platform category id for flat catalogues,
            // or a department id (a top-level subcategory) for sellers whose nav is
            // category -> department -> leaf. Accept either so both shapes filter.
            if (filters.category !== "all") {
                const inGroup =
                    p.category?._id === filters.category ||
                    p.subcategories?.some((sc) => String(sc._id) === filters.category);
                if (!inGroup) return false;
            }

            // Subcategory
            if (filters.subcategory !== "all") {
                const hasSubcat = p.subcategories?.some((sc) => sc._id === filters.subcategory);
                if (!hasSubcat) return false;
            }

            // Price ranges (any selected range must match — OR logic)
            if (filters.priceRanges.length > 0) {
                const price = effectivePrice(p);
                const matchesAnyPrice = filters.priceRanges.some((r) => matchesPriceRange(price, r));
                if (!matchesAnyPrice) return false;
            }

            // Curator's pick (any selected curator must match — OR logic)
            if (filters.curators.length > 0) {
                const matchesAnyCurator = filters.curators.some((c) => matchesCurator(p, c));
                if (!matchesAnyCurator) return false;
            }

            return true;
        });
    }, [baseProducts, filters]);

    // ── Server-side paging ────────────────────────────────────────────────────
    // Gated on a capability probe rather than assumed. The paged `/api/products`
    // call has always worked, but `priceRanges` and `/api/products/facets` ship
    // together with it — and until they are deployed, `priceRanges` is SILENTLY
    // IGNORED (a price filter that does nothing) and facet counts are unavailable
    // (so pills would count only the current page: "Vegetables 24" instead of 86).
    // Wrong numbers are worse than a big payload, so we only switch to server
    // paging once facets answer. Until then this page behaves exactly as before.
    const [facets, setFacets] = useState<ProductFacets | null>(null);
    const [serverCapable, setServerCapable] = useState<boolean | null>(null);
    const [pageProducts, setPageProducts] = useState<Product[] | null>(null);
    const [pagination, setPagination] = useState<ProductPagination | null>(null);
    const [page, setPage] = useState(1);
    const [loadingPage, setLoadingPage] = useState(false);

    /* `<= 1` treats a MISSING outlets[] as single-outlet: a portal-api that
     * predates multi-outlet returns none, and `=== 1` would strip paging,
     * facets and the count line from every ordinary store. */
    const singleOutlet = (outlets?.length ?? 0) <= 1;

    /* Memoized: a fresh array each render is a new reference, and this feeds a
     * fetch effect's dependency list — inline `.map()` would re-fetch the
     * category tree on every render. */
    const allOutletIds = useMemo(
        () => (outlets ?? NO_OUTLETS).map((o) => o.outletId),
        [outlets]
    );

    // Curated collections are an explicit, ordered id list — paging does not apply.
    const pagingEligible = !isCollection && !!servingOutletId;

    const knownCategoryIds = useMemo(
        () => new Set(categories.map((c) => String(c._id))),
        [categories]
    );

    /**
     * A selected "curator" is either a real `metadata.tags` value (when the bar
     * derived its list from products) or one of the synthetic fallback ids
     * (bestsellers / new / gifting / sale) that only `matchesCurator` understands.
     * Sending a synthetic id as `tags` would match nothing, so when any selected
     * curator is not a known tag we stay on the client path instead of silently
     * dropping the filter.
     */
    const curatorsAreServerFilterable = useMemo(() => {
        if (filters.curators.length === 0) return true;
        const known = new Set((facets?.tags ?? []).map((t) => t.value));
        return filters.curators.every((c) => known.has(c));
    }, [filters.curators, facets]);

    const serverMode = pagingEligible && serverCapable === true && curatorsAreServerFilterable;

    const query = useMemo<ProductPageQuery>(() => {
        /* A multi-branch store queries the whole TENANT so the grid can show
         * every branch's catalogue. Paging deliberately stays on — there is no
         * client-side pager in this app, so turning it off would render the
         * entire catalogue at once.
         *
         * `unique: true` moves the collapse to the service, which is what makes
         * the count line honest: the total now counts CARDS, not documents, so
         * it no longer claims more products than the grid can render. The client
         * still groups each page it receives — see `visibleProducts` — because a
         * service build predating the flag would ignore it and send the raw
         * union. A single-outlet store is a provable no-op either way. */
        const q: ProductPageQuery = singleOutlet
            ? { outletId: servingOutletId, limit: PAGE_SIZE, unique: true }
            : { tenantId: tenant.tenantId, limit: PAGE_SIZE, unique: true };
        // The leaf is the most specific selection; it already implies its department,
        // and `subcategory` is OR-ed server-side so sending both would widen, not narrow.
        if (filters.subcategory !== "all") {
            q.subcategory = filters.subcategory;
        } else if (filters.category !== "all") {
            if (knownCategoryIds.has(filters.category)) q.category = filters.category;
            else q.subcategory = filters.category; // a department id lives in subcategories[]
        }
        if (filters.priceRanges.length) q.priceRanges = filters.priceRanges;
        if (filters.curators.length) q.tags = filters.curators;
        if (activeSortBy === "price-low") { q.sortBy = "price"; q.sortOrder = "asc"; }
        else if (activeSortBy === "price-high") { q.sortBy = "price"; q.sortOrder = "desc"; }
        else if (activeSortBy === "newest") { q.sortBy = "createdAt"; q.sortOrder = "desc"; }
        // Tenant merchandising order. Only meaningful while browsing the whole
        // catalogue — once the buyer picks a category they have expressed their
        // own priority, and re-ranking underneath them would be surprising.
        // A curated collection carries its own hand-picked order, so skip there too.
        if (productPriority?.length && !isCollection &&
            filters.category === "all" && filters.subcategory === "all") {
            q.priority = productPriority;
        }
        return q;
    }, [servingOutletId, singleOutlet, tenant.tenantId, filters, activeSortBy, knownCategoryIds, productPriority, isCollection]);

    /* Capability probe + catalogue-wide facets. Scoped the same way as `query`
     * above: tenant-wide for a multi-branch store, outlet-wide otherwise. These
     * counts sit on the category pills, so scoping them to one outlet made the
     * pills under-report — "Beauty & Personal Care 27" when the tenant actually
     * has more once the other branch is included. Deliberately NOT narrowed by
     * the active filters, so the counts stay stable while a pill is selected. */
    useEffect(() => {
        if (!pagingEligible) { setServerCapable(false); return; }
        let cancelled = false;
        // Same scope as `query` — pill counts must describe the catalogue the
        // grid can actually show, or a pill reads "Snacks 4" over a 2-item grid.
        const scope = singleOutlet
            ? { outletId: servingOutletId, unique: true }
            : { tenantId: tenant.tenantId, unique: true };
        getProductFacets(scope).then((f) => {
            if (cancelled) return;
            setFacets(f);
            setServerCapable(!!f);
        });
        return () => { cancelled = true; };
    }, [pagingEligible, servingOutletId, singleOutlet, tenant.tenantId]);

    // Any filter or sort change invalidates the current page number.
    useEffect(() => { setPage(1); }, [query]);

    useEffect(() => {
        if (!serverMode) { setPageProducts(null); setPagination(null); return; }
        let cancelled = false;
        setLoadingPage(true);
        getProductPage({ ...query, page }).then((res) => {
            if (cancelled) return;
            setLoadingPage(false);
            if (!res) {
                // Endpoint went away mid-session — drop back to the client path
                // rather than showing an empty grid.
                setServerCapable(false);
                setPageProducts(null);
                setPagination(null);
                return;
            }
            setPageProducts(res.products);
            setPagination(res.pagination);
        });
        return () => { cancelled = true; };
    }, [serverMode, query, page]);

    // What the grid actually renders, before outlet resolution.
    const renderedProducts = serverMode && pageProducts ? pageProducts : filteredProducts;

    /* Belt and braces. The service was asked for `unique: true`, so this is a
     * no-op on a current build — but seller-web can deploy ahead of the product
     * service, and an older build ignores the flag and returns the raw union.
     * Grouping is idempotent, so running it again costs nothing and stops that
     * deploy window from putting duplicate cards on the page. Client mode is
     * already grouped upstream at `baseProducts`. */
    const visibleProducts = useMemo(
        () => (serverMode ? dedupeToUnique(renderedProducts) : renderedProducts),
        [serverMode, renderedProducts]
    );
    const totalCount = serverMode && pagination ? pagination.total : filteredProducts.length;
    const rangeStart = serverMode && pagination && totalCount > 0 ? (pagination.page - 1) * pagination.limit + 1 : 1;
    const rangeEnd = serverMode && pagination ? Math.min(pagination.page * pagination.limit, totalCount) : totalCount;
    const totalPages = serverMode && pagination ? pagination.pages : 1;

    return (
        <div
            className="w-full"
            style={{ background: "var(--rev-white)", color: "var(--techno-black)" }}
        >
            {/* All page content inside the container */}
            <div className="gl-container">
                <div className="mt-[clamp(40px,5vw,56px)]">
                    {isCollection ? (
                        // Curated collection: title heading, no category filter bar.
                        <>
                            <a
                                href={`/shop/all-products?prefix=${encodeURIComponent(prefix)}`}
                                className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold"
                                style={{ color: "var(--navy)" }}
                            >
                                ← All products
                            </a>
                            <h1
                                className="mt-3 leading-tight text-[clamp(24px,3vw,36px)]"
                                style={{ fontFamily: "var(--font-display)", fontWeight: 600, color: "var(--techno-black)" }}
                            >
                                {collectionTitle || "Collection"}
                            </h1>
                            <p className="mt-1 text-sm" style={{ color: "var(--techno-black-50)" }}>
                                {filteredProducts.length} product{filteredProducts.length === 1 ? "" : "s"}
                            </p>
                        </>
                    ) : (
                        <CategoryFilterBar
                            categories={categories}
                            products={baseProducts}
                            /* Every landing outlet, not just the serving one: the tree's
                               `productIds` become the category filter's predicate, and a
                               winner held by another branch would otherwise be filtered
                               out the moment a buyer taps a category tile. */
                            outletId={servingOutletId}
                            outletIds={allOutletIds}
                            onFilterChange={setFilters}
                            facets={facets}
                            initialCategory={initialCategory}
                        />
                    )}
                    {serverMode && (
                        <p className="mt-5 text-sm" style={{ color: "var(--techno-black-50)" }}>
                            {totalCount === 0
                                ? "No products match these filters"
                                : `Showing ${rangeStart}–${rangeEnd} of ${totalCount} product${totalCount === 1 ? "" : "s"}`}
                        </p>
                    )}
                    <div
                        className="mt-7"
                        style={{ opacity: loadingPage ? 0.55 : 1, transition: "opacity 150ms" }}
                        aria-busy={loadingPage}
                    >
                        <AllProducts
                            products={visibleProducts}
                            outletId={outletId}
                            /* Undefined so AllProducts falls back to its own
                               `hasMultipleOutlets(productList)`, which inspects the
                               rendered page — the only signal that stays correct once a
                               grid can mix branches. An explicit value WINS over that
                               fallback. Matches ShopClient's data bag. */
                            multiOutlet={undefined}
                            /* In server mode the server already sorted, so hand the grid a
                               no-op sort key rather than let it re-sort the page it was
                               given. "newest" is that no-op: it preserves incoming order. */
                            sortBy={serverMode ? "newest" : activeSortBy}
                            /* Server mode already applied the tenant order in the
                               query; re-applying it to a single page would only
                               shuffle that page. Client mode has the full set, so
                               it ranks here instead. */
                            priority={serverMode ? undefined : productPriority}
                            prefix={prefix}
                            showViewAll={false}
                        />
                    </div>
                    {serverMode && totalPages > 1 && (
                        <nav
                            className="mt-8 flex items-center justify-center gap-2"
                            aria-label="Pagination"
                        >
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1 || loadingPage}
                                className="rounded-lg border px-4 py-2 text-[13px] font-semibold disabled:opacity-40"
                                style={{ borderColor: "var(--border)", color: "var(--techno-black)" }}
                            >
                                Previous
                            </button>
                            <span className="px-2 text-[13px]" style={{ color: "var(--techno-black-50)" }}>
                                Page {page} of {totalPages}
                            </span>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages || loadingPage}
                                className="rounded-lg border px-4 py-2 text-[13px] font-semibold disabled:opacity-40"
                                style={{ borderColor: "var(--border)", color: "var(--techno-black)" }}
                            >
                                Next
                            </button>
                        </nav>
                    )}
                </div>
            </div>
        </div>
    );
}
